import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, User, Bell, Moon, Mail, FileText, Shield, LogOut, LogIn } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { getUserPreferences, saveUserPreferences, clearUserData } from '../utils/userPreferences';
import { clearAllDownloads } from '../utils/audioLibrary';
import { useTheme } from '../context/ThemeContext';
import { App as CapacitorApp } from '@capacitor/app';

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { darkMode, toggleDarkMode } = useTheme();
  const [notifications, setNotifications] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const stored = getUserPreferences();
    setNotifications(stored.notifications);
    
    // Check if user is authenticated
    checkAuthStatus();
  }, []);

  // Handle Android back button
  useEffect(() => {
    let backButtonListener;
    
    const setupBackButton = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', () => {
        navigate('/');
      });
    };
    
    setupBackButton();

    return () => {
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, [navigate]);

  const checkAuthStatus = async () => {
    // First check localStorage for auth token and user data
    const authToken = localStorage.getItem('smartreadgo_auth_token');
    const storedUser = getUserPreferences();
    
    if (authToken && storedUser.email) {
      // User is authenticated via localStorage
      setIsAuthenticated(true);
      setCurrentUser({
        email: storedUser.email,
        name: storedUser.name || '',
        mobile: storedUser.mobile || '',
        age: storedUser.age || ''
      });
      return;
    }
    
    // Fallback: try to check via API (for web/cookie-based auth)
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setIsAuthenticated(true);
        setCurrentUser(userData);
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setCurrentUser(null);
    }
  };

  const handleToggleNotifications = (e) => {
    e.stopPropagation();
    const newValue = !notifications;
    setNotifications(newValue);
    saveUserPreferences({ notifications: newValue, darkMode });
    
    toast({
      title: newValue ? 'Notifications Enabled' : 'Notifications Disabled',
      description: newValue ? 'You will receive daily digests and new article alerts' : 'You will not receive notifications',
    });
  };

  const handleToggleDarkMode = (e) => {
    e.stopPropagation();
    const newValue = toggleDarkMode();
    
    toast({
      title: newValue ? 'Dark Mode Enabled' : 'Dark Mode Disabled',
      description: newValue ? 'Easier on the eyes for night driving' : 'Light mode activated',
    });
  };

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out? This will clear all your data including downloaded articles.')) {
      try {
        // Call backend logout API
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (error) {
        console.log('Logout API error:', error);
      }
      
      // Clear local data
      clearUserData();
      clearAllDownloads();
      
      // Clear guest mode flag
      localStorage.removeItem('smartreadgo_guest_mode');
      
      setIsAuthenticated(false);
      setCurrentUser(null);
      
      toast({
        title: 'Signed Out',
        description: 'Your data has been cleared',
      });
      
      // Redirect to login page
      navigate('/login');
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  // Custom Toggle Component
  const CustomToggle = ({ checked, onChange, activeColor = 'bg-blue-500' }) => {
    return (
      <button
        type="button"
        onClick={onChange}
        className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
          checked ? activeColor : 'bg-gray-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    );
  };

  return (
    <div className={`min-h-screen pb-20 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`border-b ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Safe area padding for mobile status bar */}
        <div className="pt-[env(safe-area-inset-top,12px)]" />
        <div className="px-4 py-4">
          <div className="flex items-center gap-3 mb-1">
            <div className={`p-2 rounded-full ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
              <svg className={`w-6 h-6 ${darkMode ? 'text-orange-500' : 'text-orange-600'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Settings</h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage your preferences</p>
            </div>
          </div>
        </div>
      </header>

      {/* Menu Items */}
      <div className="px-4 py-4 space-y-6">
        {/* Account Section */}
        <div>
          <h2 className={`text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Account</h2>
          <div className={`rounded-lg shadow-sm overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <button
              onClick={() => navigate('/settings/profile')}
              className={`w-full px-4 py-4 flex items-center gap-4 transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
            >
              <div className={`p-3 rounded-full flex-shrink-0 ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                <User className={`w-6 h-6 ${darkMode ? 'text-orange-500' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Profile & Preferences</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage your account and reading preferences</p>
              </div>
              <ChevronRight className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </button>
          </div>
        </div>

        {/* App Preferences Section */}
        <div>
          <h2 className={`text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>App Preferences</h2>
          <div className={`rounded-lg shadow-sm overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Notifications Toggle */}
            <div className={`px-4 py-4 flex items-center gap-4 ${darkMode ? 'border-gray-700' : 'border-gray-100'} border-b`}>
              <div className={`p-3 rounded-full flex-shrink-0 ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                <Bell className={`w-6 h-6 ${darkMode ? 'text-orange-500' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Notifications</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Daily digest and new article alerts</p>
              </div>
              <CustomToggle
                checked={notifications}
                onChange={handleToggleNotifications}
                activeColor="bg-blue-500"
              />
            </div>

            {/* Light Mode Toggle (shows "Light Mode" when in dark, "Dark Mode" when in light) */}
            <div className="px-4 py-4 flex items-center gap-4">
              <div className={`p-3 rounded-full flex-shrink-0 ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                <Moon className={`w-6 h-6 ${darkMode ? 'text-orange-500' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {darkMode ? 'Light Mode' : 'Dark Mode'}
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {darkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                </p>
              </div>
              <CustomToggle
                checked={darkMode}
                onChange={handleToggleDarkMode}
                activeColor="bg-orange-500"
              />
            </div>
          </div>
        </div>

        {/* Support & Legal Section */}
        <div>
          <h2 className={`text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Support & Legal</h2>
          <div className={`rounded-lg shadow-sm overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Contact Us */}
            <button
              onClick={() => navigate('/settings/contact')}
              className={`w-full px-4 py-4 flex items-center gap-4 transition-colors border-b ${darkMode ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-100'}`}
            >
              <div className={`p-3 rounded-full flex-shrink-0 ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                <Mail className={`w-6 h-6 ${darkMode ? 'text-orange-500' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact Us</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Get help and support</p>
              </div>
              <ChevronRight className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </button>

            {/* Terms & Conditions */}
            <button
              onClick={() => navigate('/settings/terms')}
              className={`w-full px-4 py-4 flex items-center gap-4 transition-colors border-b ${darkMode ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-100'}`}
            >
              <div className={`p-3 rounded-full flex-shrink-0 ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                <FileText className={`w-6 h-6 ${darkMode ? 'text-orange-500' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Terms & Conditions</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Read our terms of service</p>
              </div>
              <ChevronRight className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </button>

            {/* Privacy Policy */}
            <button
              onClick={() => navigate('/settings/privacy')}
              className={`w-full px-4 py-4 flex items-center gap-4 transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
            >
              <div className={`p-3 rounded-full flex-shrink-0 ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                <Shield className={`w-6 h-6 ${darkMode ? 'text-orange-500' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Privacy Policy</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>How we protect your data</p>
              </div>
              <ChevronRight className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </button>

            {/* Takedown Policy */}
            <button
              onClick={() => navigate('/settings/takedown')}
              className={`w-full flex items-center gap-4 p-4 rounded-lg transition-colors ${
                darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
              }`}
            >
              <div className={`p-3 rounded-full ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <Shield className={`w-6 h-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Takedown Policy</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Content removal requests</p>
              </div>
              <ChevronRight className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </button>
          </div>
        </div>

        {/* Removed Sign Out / Login Section - Users stay as guests or use Profile to add details */}

        {/* App Version */}
        <div className="text-center py-6 space-y-1">
          <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>SmartReadGo v1.0.0</p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Built for safe, hands-free reading</p>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav activeTab="settings" darkMode={darkMode} />
    </div>
  );
};

export default Settings;